import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Col, Dropdown, FormLabel, Row } from 'react-bootstrap';
import FilterAccordion from "../../../Common/Filter/FilterAccordion";
import InputDate from "../../../Forms/Element/InputDate";
import { errorAlert, warnAlert } from "../../../alert/defaultAlert";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import GridCustomBase from "../../../GridCustom/GridCustomBase";
import { formatDateTime, formatDateToYYYYMMDD } from "../../../../../utils/DateUtils";
import Common from "../../../../../constants/Common";
import InputText from "../../../Forms/Element/InputText";
import SearchIcon from "../../../Icon/SearchIcon";
import { useNavigate } from "react-router-dom";
import MenuConfig from "../../../../../constants/MenuConfig";
import SaveIcon from "../../../Icon/SaveIcon";
import { useBranch } from "../../../../../context/BranchContext";
import StockMovementTypeSelect from "./StockMovementTypeSelect";
import StockMovementType from "./StockMovementType";
import BranchSelect from "../../Main/Branch/BranchSelect";

const RestockHistory = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [product, setProduct] = useState('');
    const [type, setType] = useState(null);
    const [branchFilter, setBranchFilter] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const navigate = useNavigate();

    // Grid
    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);

    function handleClearFilters() {
        setStartDate(new Date());
        setEndDate(new Date());
        setProduct('');
        setType(null);
        // Preserva a filial do contexto, se houver
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }
        setPage(0);
    }

    function search() {
        let filters = [];
        if (!startDate) {
            warnAlert(t('validation.required.start_date'));
            return;
        }
        if (!endDate) {
            warnAlert('validation.required.end_date');
            return;
        }

        filters.push({
            field: "createdAt",
            value: formatDateToYYYYMMDD(startDate) + "T00:00:00",
            operation: SearchOperation.GREATER_THAN_EQUAL
        });
        filters.push({
            field: "createdAt",
            value: formatDateToYYYYMMDD(endDate) + "T23:59:59",
            operation: SearchOperation.LESS_THAN_EQUAL
        });

        // Adiciona a filial. 
        // Deve trazer apenas os pedidos da filial selecionada.
        // Se há contexto, usa ele. Senão, usa branchFilter (se selecionado)
        const branchToFilter = selectedBranch !== null ? selectedBranch : branchFilter;
        
        if (branchToFilter !== null) {
            filters.push({
                field: "branch.id",
                value: branchToFilter.value || branchToFilter.id,
                operation: SearchOperation.EQUAL
            });
        }

        if (product) {
            filters.push({
                field: "product.name",
                value: product,
                operation: SearchOperation.MATCH
            });
        }

        if (type) {
            if (type.value === StockMovementType.IN) {
                filters.push({
                    field: "type",
                    operation: SearchOperation.EQUAL,
                    value: StockMovementType.IN
                });
            } else if (type.value === StockMovementType.OUT) {
                filters.push({
                    field: "type",
                    operation: SearchOperation.EQUAL,
                    value: StockMovementType.OUT
                });
            } else {
                filters.push({
                    field: "type",
                    operation: SearchOperation.EQUAL,
                    value: StockMovementType.ADJUSTMENT
                });
            }
        }

        let payload = {
            filters: filters,
            page: page,
            size: size,
            orders: [{
                field: "id",
                direction: "DESC"
            }]
        }

        setShowLoading(true);
        axiosInstance.post(Api.stock_control.restock + "/pageable", payload)
            .then((response) => {
                setPageable(response.data);
                setRows(response.data.content);
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    }

    function redirectToNew() {
        navigate(MenuConfig.stock_control.restock.route + "/new");
    }

    // Sincroniza o filtro de filial com o contexto quando ele muda
    useEffect(() => {
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }
    }, [selectedBranch]);

    useEffect(() => {
        search();
    }, [page, size, selectedBranch]);

    return (
        <>
            <FilterAccordion
                title={t('filters')}
                onSearch={search}
                onClear={handleClearFilters}
                showQuickDates={true}
                onQuickDateSelect={(start, end) => {
                    setStartDate(start);
                    setEndDate(end);
                }}
            >
                <Row>
                    <Col md={2}>
                        <FormLabel>{t('start_date')}</FormLabel>
                        <InputDate
                            onChange={(value) => {
                                setStartDate(value);
                            }}
                            selected={startDate}
                        />
                    </Col>
                    <Col md={2}>
                        <FormLabel>{t('end_date')}</FormLabel>
                        <InputDate
                            onChange={(value) => {
                                setEndDate(value);
                            }}
                            selected={endDate}
                        />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t('product')}</FormLabel>
                        <InputText
                            value={product}
                            onChange={(e) => {
                                setProduct(e.target.value);
                            }}
                        />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t('branch')}</FormLabel>
                        <BranchSelect
                            value={branchFilter}
                            onChange={(value) => {
                                setBranchFilter(value);
                            }}
                            isDisabled={selectedBranch !== null}
                            isClearable={true}
                        />
                    </Col>
                    <Col md={2}>
                        <FormLabel>{t('operation')}</FormLabel>
                        <StockMovementTypeSelect
                            value={type}
                            onChange={(value) => {
                                setType(value);
                            }}
                            isClearable={true}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <Row className="row mb-3">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => redirectToNew()}><SaveIcon /> {t('new')}</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>
            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}>

                <table className="table table-list i-table style-1 mb-0 border-0">
                    <thead>
                        <tr>
                            <th style={{ width: 150 }}>{t("operation")}</th>
                            <th style={{ width: 300 }}>{t("product")}</th>
                            <th style={{ width: 100 }}>{t("qty")}</th>
                            <th>{t("date_time")}</th>
                            <th>{t("description")}</th>
                            <th>{t("entered_by")}</th>
                            <th className="bg-none"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index} className={
                                "align-middle h6 "}>
                                <td className={
                                    (row.type === StockMovementType.IN ? 'text-success'
                                        : row.type === StockMovementType.OUT ? 'text-danger' : '')
                                }>{t(row.type.toLowerCase())}</td>
                                <td>{row.product.name}</td>
                                <td className={
                                    (row.type === StockMovementType.IN ? 'text-success'
                                        : row.type === StockMovementType.OUT ? 'text-danger' : '')
                                }>
                                    {
                                        (row.type === StockMovementType.IN ? '+'
                                            : row.type === StockMovementType.OUT ? '-' : '')
                                    }
                                    {row.qty}
                                </td>
                                <td>
                                    {formatDateTime(row.createdAt)}
                                </td>
                                <td>
                                    {row.description}
                                </td>
                                <td>
                                    <strong>{row.createdBy}</strong>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
}

export default RestockHistory;